import { complement, differenceWith, isNil, pipe, pick, omit } from 'ramda';
import { v4 as uuidv4 } from 'uuid';

import {
  addRequestTypes,
  requestOriginTypes,
  requestTypes,
  responseTypes,
} from '../enums/requestTypes';
import {
  createNewStop,
  findKingParcel,
  getStopParcelCodesWithRouteDate,
  getRouteDateForParcelFromRequest,
  getStopAndStopIndexByStopNumber,
} from './stop';
import { isDeferredRequest } from '../features/routeDetail/model';
import {
  accumRemainingStopParcelCodes,
  checkIsEqualParcelCode,
  getRelatedInitialParcels,
} from './parcel';
import { NO_STOP_INDEX } from '../constants/variables';
import {
  getSelectedToteLabelNumbers,
  parcelsConsignmentValues,
} from './editStop';
import {
  arrayToArrayOfObjectsByValue,
  arrayToObjectByValue,
} from '../utils/array';

export const generateRequest = ({
  requestType,
  parcel = {},
  stop = {},
  routeId = null,
  requestOrder,
}) => ({
  routeId,
  requestType,
  requestId: uuidv4(),
  labelNumber: parcel.labelNumber || null,
  parcelCode: parcel.parcelCode || null,
  routeDate: parcel.routeDate || stop.collection?.routeDate,
  stopNumber: stop.stopNumber || null,
  sourceStopId: stop.stopId || null,
  collectionCode: stop.collection?.collectionCode || null,
  requestOrigin: requestOriginTypes.ROUTE_PLANNER,
  order: requestOrder,
});

export const setResponse =
  ({
    responseType,
    originalStopNumber = null,
    newStopNumber = null,
    originalRouteId = null,
    newRouteId = null,
    sourceStopId = null,
  }) =>
  (request) => ({
    ...request,
    sourceStopId: !isNil(request.sourceStopId)
      ? request.sourceStopId
      : sourceStopId,
    response: {
      responseType,
      originalStopNumber,
      newStopNumber,
      originalRouteId,
      newRouteId,
    },
  });

export const resetResponse = (request) => omit(['response'], request);

export const isRequestFromRoutePlanner = ({ requestOrigin }) =>
  requestOrigin === requestOriginTypes.ROUTE_PLANNER;

export const isRequestFromSaturn = ({ requestOrigin }) =>
  requestOrigin === requestOriginTypes.SATURN;

export const getRequestsParcelCodes = (requests = []) =>
  requests.reduce(
    (acc, item) =>
      !!item && item.parcelCode ? [...acc, item.parcelCode] : acc,
    []
  );

export const getRequestsByParcelCodes = (requests, parcelCodes, requestType) =>
  requests.filter((r) =>
    !r.response || r.response.responseType === responseTypes.DEFER
      ? parcelCodes.find((parcelCode) =>
          requestType
            ? r.parcelCode === parcelCode && r.requestOrigin === requestType
            : r.parcelCode === parcelCode
        )
      : false
  );

export const getDeferredRequests = (requests = []) =>
  requests.filter(
    (request) =>
      request.response && request.response.responseType === responseTypes.DEFER
  );

export const setDeferRequest = (stop) => (request) =>
  setResponse({
    responseType: responseTypes.DEFER,
    sourceStopId: stop?.stopId || null,
  })(request);

export const setRejectRequest = (request, stop) =>
  setResponse({
    responseType: responseTypes.REJECT,
    newRouteId: isRequestFromRoutePlanner(request) ? '000' : null,
    originalStopNumber: stop?.stopNumber,
    sourceStopId: stop?.stopId || null,
  })(request);

export const generateTransferResponse = (
  routeId,
  selectedRouteId,
  stopNumber,
  sourceStopId,
  responseType
) =>
  setResponse({
    responseType,
    originalStopNumber: stopNumber,
    originalRouteId: routeId,
    newRouteId: selectedRouteId,
    sourceStopId,
  });

export const isPlannedAddRequest = (request) =>
  addRequestTypes.includes(request.requestType) &&
  !isRequestFromRoutePlanner(request) &&
  request.plannedRoute;

export const hasAnyPlannedAddRequest = (requests = []) =>
  requests.some(isPlannedAddRequest);

export const isNotPlannedAddRequest = (request) =>
  addRequestTypes.includes(request.requestType) &&
  !isRequestFromRoutePlanner(request) &&
  isNil(request.plannedRoute);

export const isPKRequest = (request) =>
  request.requestType === requestTypes.AMEND_PICKUP_PARCELS;

export const isShopUnsuitableRequest = (request) =>
  isPKRequest(request) && !isNil(request.address);

export const getPlannedRequestsForReject = (requests) =>
  requests.filter(complement(isNotPlannedAddRequest));

export const generateMoveParcelRequests = (
  parcels,
  fromStop,
  toStop,
  routeId,
  originalParcels,
  requestOrder
) => {
  const newRequests = [];

  (parcels || []).forEach((parcel) => {
    newRequests.push({
      ...setResponse({
        responseType: responseTypes.ACCEPT,
        originalStopNumber: fromStop.stopNumber,
        sourceStopId: fromStop.stopId,
        newStopNumber: toStop.stopNumber,
        originalRouteId: routeId,
        newRouteId: routeId,
      })(
        generateRequest({
          requestType: requestTypes.ADD_PARCEL,
          parcel,
          stop: toStop,
          requestOrder,
        })
      ),
      sourceParcelStopId: originalParcels[parcel.parcelCode].sourceParcelStopId,
    });
  });

  return newRequests;
};

export const updateRouteRequestsWithMapper = (
  routeRequests,
  requests,
  mapper,
  stop
) => {
  const updateRequestsMap = requests.reduce((acc, item) => {
    acc[item.requestId] = item;
    return acc;
  }, {});

  return routeRequests.map((request) =>
    updateRequestsMap[request.requestId] ? mapper(request, stop) : request
  );
};

export const generateCollectionStopTransferRequests = (
  routeId,
  transferRouteId,
  stop,
  requestOrder
) => [
  setResponse({
    responseType: responseTypes.ACCEPT,
    originalStopNumber: stop.stopNumber,
    originalRouteId: routeId,
    newRouteId: transferRouteId,
    sourceStopId: stop.stopId,
  })(
    generateRequest({
      requestType: requestTypes.REMOVE_COLLECTION,
      requestOrder,
      stop,
    })
  ),
];

export const generateRequestToAddParcelToTransferRoute = (
  parcels,
  transferRouteId,
  stop,
  requestOrder
) => {
  const leadParcel = findKingParcel(parcels) || parcels[0];
  const relatedStopParcels = parcels
    .filter((parcel) => parcel.parcelCode !== leadParcel.parcelCode)
    .map((parcel) =>
      pick(
        [
          'parcelCode',
          'labelNumber',
          'serviceEndTime',
          'serviceStartTime',
          'routeDate',
          'sourceParcelStopId',
        ],
        parcel
      )
    );
  const fullStopTransfer = parcels.length === stop.parcel.length;
  const requestBody = {
    ...generateRequest({
      requestType: requestTypes.ADD_PARCEL,
      parcel: leadParcel,
      stop,
      requestOrder,
    }),
    routeId: transferRouteId,
    serviceStartTime: leadParcel?.serviceStartTime,
    serviceEndTime: leadParcel?.serviceEndTime,
    relatedStopParcels,
    fullStopTransfer,
    sourceParcelStopId: leadParcel?.sourceParcelStopId,
  };
  return fullStopTransfer
    ? { ...requestBody, stopTime: stop.stopTime }
    : requestBody;
};

export const generateMergeToStopRequests = (requests, routeId, requestOrder) =>
  requests.map((request) => {
    const newRequestType =
      request.requestType === requestTypes.REMOVE_COLLECTION
        ? requestTypes.ADD_COLLECTION
        : requestTypes.ADD_PARCEL;

    return generateRequest({
      requestType: newRequestType,
      parcel: request,
      stop: { collection: { collectionCode: request.collectionCode } },
      routeId,
      requestOrder,
    });
  });

export const processParcelTransferToRequests = (
  transferRouteId,
  stop,
  originalParcels,
  parcels,
  requestOrder
) => {
  const transferParcels = getRelatedInitialParcels({
    stop,
    originalParcels,
    processedParcels: parcels,
  });

  return [
    generateRequestToAddParcelToTransferRoute(
      transferParcels,
      transferRouteId,
      stop,
      requestOrder
    ),
  ];
};

export const getValidateParcelList = (stop, requests) =>
  differenceWith(
    (array1, array2) => array1.parcelCode === array2.parcelCode,
    getStopParcelCodesWithRouteDate(stop),
    requests
  );

export const isAddParcelRequest = (request) =>
  request?.requestType === requestTypes.ADD_PARCEL;

export const isAddCollectionRequest = (request) =>
  request?.requestType === requestTypes.ADD_COLLECTION;

export const isAddRequest = (request) =>
  isAddParcelRequest(request) || isAddCollectionRequest(request);

export const isPendingRequest = (parcelCode) => (request) =>
  request.parcelCode === parcelCode &&
  ((request.originType === requestOriginTypes.SATURN &&
    isDeferredRequest(request)) ||
    (isAddParcelRequest(request) &&
      (!request?.response || isDeferredRequest(request))));

export const filterPendingRequests = (stopParcelCodes, requests) =>
  stopParcelCodes.filter(
    ({ parcelCode }) => !requests.find(isPendingRequest(parcelCode))
  );

export const getParcelsWithUpdatedParcelIndexes = (parcels, parcelsFromAPI) => {
  const parcelCodesFromAPI = new Set(
    parcelsFromAPI.map(({ parcelCode }) => parcelCode)
  );
  const restParcels = parcels.filter(
    ({ parcelCode }) => !parcelCodesFromAPI.has(parcelCode)
  );
  const originalParcelIndexes = new Set(
    restParcels.map((parcel) => parcel.parcelIndex)
  );
  const hasCommonParcelIndex = parcelsFromAPI.some((parcel) =>
    originalParcelIndexes.has(parcel.parcelIndex)
  );

  if (hasCommonParcelIndex) {
    const maxIndex = Math.max(
      ...parcelsFromAPI.map((parcel) => parcel.parcelIndex)
    );
    return [
      ...restParcels.map((parcel, idx) => ({
        ...parcel,
        parcelIndex: maxIndex + 1 + idx,
      })),
      ...parcelsFromAPI,
    ];
  }

  return [...restParcels, ...parcelsFromAPI];
};

export const getUpdatedParcels = (parcels, parcelsFromAPI) => {
  const parcelCodesFromAPI = new Set(
    parcelsFromAPI.map(({ parcelCode }) => parcelCode)
  );
  return [
    ...parcels.filter(({ parcelCode }) => !parcelCodesFromAPI.has(parcelCode)),
    ...parcelsFromAPI,
  ];
};

export const isRequestWithAddress = (requestAddress) => !isNil(requestAddress);

export const getRemovalRequestData = (data) => {
  const remainingStopParcels = data.processStop.parcel.reduce(
    accumRemainingStopParcelCodes(data.processedRequest.parcelCode),
    []
  );

  const removalData = remainingStopParcels.length
    ? remainingStopParcels
    : data.processStop.stopNumber;

  return {
    ...data,
    [remainingStopParcels.length ? 'validateParcels' : 'removeStopNumber']:
      removalData,
  };
};

const findPKParcel = (data) => {
  const parcel = data.processStop.parcel.find(
    checkIsEqualParcelCode(data.processedRequest.parcelCode)
  );
  return parcel
    ? [{ parcelCode: parcel.parcelCode, routeDate: parcel.routeDate }]
    : [];
};

export const getRemovedAcceptPKParcels = (data) => ({
  ...data,
  removedParcels: isRequestWithAddress(data.processedRequest.address)
    ? findPKParcel(data)
    : [],
});

export const routePlannerAddParcelRequests = (routeRequests) =>
  routeRequests.filter(
    (request) =>
      isRequestFromRoutePlanner(request) &&
      isAddParcelRequest(request) &&
      (!request.response ||
        request.response.responseType === responseTypes.DEFER)
  );

export const onAcceptPKRequest = ({
  routeId,
  processStop,
  processedRequest,
}) => {
  const { removedParcels, validateParcels, removeStopNumber } = pipe(
    getRemovedAcceptPKParcels,
    getRemovalRequestData
  )({
    processStop,
    processedRequest,
  });

  const updatedRequests = [
    setResponse({
      responseType: responseTypes.ACCEPT,
      originalStopNumber: processedRequest.stopNumber,
      sourceStopId: processStop.stopId,
      originalRouteId: routeId,
      newRouteId: isRequestWithAddress(processedRequest.address)
        ? routeId
        : 'GOH',
    })(processedRequest),
  ];

  return {
    removedParcels,
    updatedRequests,
    validateParcels,
    removeStopNumber,
  };
};

export const onAcceptACRequest = ({
  stop,
  stops,
  routeId,
  processedRequest,
  processStopIndex,
}) => {
  const newStop =
    processStopIndex === NO_STOP_INDEX
      ? createNewStop({ stops, request: processedRequest })
      : undefined;

  const updatedRequests = [
    setResponse({
      responseType: responseTypes.ACCEPT,
      originalStopNumber: processedRequest.stopNumber,
      sourceStopId: stop.stopId,
      newStopNumber: stop.stopNumber,
      newRouteId: routeId,
    })(processedRequest),
  ];

  return {
    newStop,
    updatedRequests,
  };
};

export const onAcceptDefaultRequest = ({
  stop,
  stops,
  routeId,
  requests,
  routeRequests,
  processStopIndex,
  processStop,
  routeDate,
}) => {
  const validateParcels =
    processStopIndex > NO_STOP_INDEX
      ? filterPendingRequests(
          getStopParcelCodesWithRouteDate(stops[processStopIndex]),
          routeRequests
        )
      : [];

  const updatedRequests = requests.map((request) => {
    validateParcels.push({
      parcelCode: request.parcelCode,
      routeDate: getRouteDateForParcelFromRequest(request, stops, routeDate),
    });
    return {
      ...setResponse({
        responseType: responseTypes.ACCEPT,
        originalStopNumber: request.stopNumber,
        sourceStopId: processStop?.stopId,
        newStopNumber: stop.stopNumber,
        newRouteId: routeId,
      })(request),
    };
  });

  return {
    updatedRequests,
    validateParcels,
  };
};

export const processAcceptRequest = (data) => {
  switch (data.processedRequest.requestType) {
    case requestTypes.AMEND_PICKUP_PARCELS: {
      return onAcceptPKRequest(data);
    }
    case requestTypes.ADD_COLLECTION: {
      return onAcceptACRequest(data);
    }
    default: {
      return onAcceptDefaultRequest(data);
    }
  }
};

export const updateRequestsResponseWithStopNumber = (
  requests,
  { stopNumber }
) =>
  requests.map((request) => ({
    ...request,
    response: { ...request.response, newStopNumber: stopNumber },
  }));

export const getRemainingParcelsFromStops = (stops, requests) => {
  const selectedParcelsCode = requests.map(({ parcelCode }) => parcelCode);
  const processStopIndex = getStopAndStopIndexByStopNumber(
    stops,
    requests[0].stopNumber
  );
  const stopParcels = stops[processStopIndex]?.parcel || [];
  return stopParcels.reduce(
    (acc, parcel) =>
      !selectedParcelsCode.includes(parcel.parcelCode)
        ? [
            ...acc,
            {
              ...parcel,
              consignmentValue: parcel.consignmentNumber + parcel.shipmentDate,
            },
          ]
        : acc,
    []
  );
};

export const getRemainingConsignmentRequests = (
  routeRequests,
  selectedRequest
) => {
  const groupedSelectedRequest = arrayToObjectByValue(
    selectedRequest,
    'stopNumber'
  );
  const groupedRouteRequest = arrayToArrayOfObjectsByValue(
    routeRequests,
    'stopNumber'
  );

  return Object.values(groupedSelectedRequest).reduce((acc, request) => {
    const selectedConsignmentValues = parcelsConsignmentValues(request);
    const selectedParcelsCode = request.map(({ parcelCode }) => parcelCode);
    const processedRequests =
      (groupedRouteRequest[request[0].stopNumber] &&
        groupedRouteRequest[request[0].stopNumber].filter(
          (item) =>
            !selectedParcelsCode.includes(item.parcelCode) &&
            item.consignmentNumber &&
            item.cadScan &&
            selectedConsignmentValues.includes(
              item.consignmentNumber + item.shipmentDate
            )
        )) ||
      [];
    return processedRequests.length ? [...acc, ...processedRequests] : acc;
  }, []);
};

export const getRemainingToteRequests = (routeRequests, selectedRequest) => {
  const selectedParcelCodes = selectedRequest.map(
    ({ parcelCode }) => parcelCode
  );
  const selectedToteLabelNumbers = getSelectedToteLabelNumbers(selectedRequest);

  return routeRequests.reduce(
    (acc, request) =>
      !selectedParcelCodes.includes(request.parcelCode) &&
      selectedToteLabelNumbers.includes(request.toteLabelNumber)
        ? [...acc, request]
        : acc,
    []
  );
};

export const getAffectedRequests = (
  routeRequests,
  selectedRequest,
  conditions
) => {
  if (!selectedRequest.length || !routeRequests.length) {
    return [];
  }

  for (let i = 0; i < conditions.length; i++) {
    const conditionFunction = conditions[i];
    const processedRequests = conditionFunction(routeRequests, selectedRequest);
    if (processedRequests.length) {
      return getAffectedRequests(
        routeRequests,
        [...selectedRequest, ...processedRequests],
        conditions
      );
    }
  }

  return selectedRequest;
};
